import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';
import isNumber from 'services/numbers/isNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import getInstallationTotalExpense from 'services/products/calculations/getInstallationTotalExpense';
import {
  getSellingPriceForMarginPercent,
  getSellingPriceQuantityFactor,
} from 'services/products/calculations/getSellingPriceForMargin';
import getSubproductTotalExpense from 'services/products/calculations/getSubproductTotalExpense';

import IVersion from '../IVersion';
import priceMaterialFromDetails from './subproducts/priceMaterialFromDetails';

/*
 * Target margin of the calculation window. The margin % at the top of the window prices
 * every row that follows it: the rows added in the window, until the user sets the price
 * or a margin of the row. Changing the target margin prices those rows again; rows priced
 * by the user keep their price. Following the target is not saved.
 */

// The margin % the window starts with
export const DEFAULT_TARGET_MARGIN_PERCENT = '25';

interface IPricedRow {
  calculatePriceFromDetails?: boolean;
  followsTargetMargin?: boolean;
  quantity: string;
  sellingPrice: string;
  sellingPriceUnit: string | null;
}

/** The row priced for the target margin, or the row as it is when that is not possible */
export const priceForTargetMargin = <T extends IPricedRow>(
  row: T,
  getExpense: (row: T) => number,
  targetMarginPercent: string
): T => {
  // An empty (or partly typed) target margin does not price the rows
  if (
    !row.followsTargetMargin ||
    row.calculatePriceFromDetails ||
    !isNumber(targetMarginPercent)
  ) {
    return row;
  }

  const expense = getExpense(row);

  // Without an expense there is nothing to price; an earlier price would show 100 %
  if (!(expense > 0)) {
    return row.sellingPrice === '' ? row : { ...row, sellingPrice: '' };
  }

  const sellingPrice = getSellingPriceForMarginPercent(
    expense,
    numericStringToFloat(targetMarginPercent),
    getSellingPriceQuantityFactor(
      row.sellingPriceUnit,
      numericStringToFloat(row.quantity)
    )
  );

  return sellingPrice === null
    ? row
    : { ...row, sellingPrice: formatFloatForDisplay(sellingPrice) };
};

/**
 * Applies the target margin to an updated row: when the user changed the selling price
 * (also through a margin field) the row stops following the target margin, otherwise a
 * following row is priced for it from its updated expense.
 */
export const withTargetMargin = <T extends IPricedRow>(
  previous: T,
  updated: T,
  getExpense: (row: T) => number,
  targetMarginPercent: string
): T => {
  if (!updated.followsTargetMargin) {
    return updated;
  }

  if (updated.sellingPrice !== previous.sellingPrice) {
    return { ...updated, followsTargetMargin: false };
  }

  return priceForTargetMargin(updated, getExpense, targetMarginPercent);
};

/**
 * Prices the following rows of a version for a new target margin. A material price
 * calculated from the details follows its priced details.
 */
export const priceVersionForTargetMargin = (
  version: IVersion,
  targetMarginPercent: string
): IVersion => ({
  ...version,
  installations: version.installations.map((installation) =>
    priceForTargetMargin(
      installation,
      getInstallationTotalExpense,
      targetMarginPercent
    )
  ),
  subproducts: version.subproducts.map((subproduct) => {
    const details = subproduct.details.map((detail) =>
      priceForTargetMargin(
        detail,
        getSubproductTotalExpense,
        targetMarginPercent
      )
    );

    if (subproduct.calculatePriceFromDetails) {
      // Only when a detail follows the target; a price set by the user is kept otherwise
      if (!details.some((detail) => detail.followsTargetMargin)) {
        return subproduct;
      }

      return priceMaterialFromDetails({ ...subproduct, details });
    }

    return priceForTargetMargin(
      { ...subproduct, details },
      getSubproductTotalExpense,
      targetMarginPercent
    );
  }),
});
